/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FocusEvent, useCallback, useMemo } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Checkbox, FormControl, FormControlLabel, IconButton } from '@mui/material';

import { useCheckboxFormWidgetStyles, useCommonFormWidgetStyles } from '../../../../../../styles/customFormWidgets';
import { CustomWidgetProps } from '../../ParametersTab.types';

// eslint-disable-next-line
export const CustomCheckboxWidget = (props: CustomWidgetProps) => {
  const { id, value, required, disabled, readonly, label, autofocus, onChange, onBlur, onFocus, schema, formContext } =
    props;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (formContext?.handleMenuOpen) {
      formContext.handleMenuOpen({ event, name });
    }
  };

  const checkboxWidgetClasses = useCheckboxFormWidgetStyles();
  const commonWidgetClasses = useCommonFormWidgetStyles();
  const name = useMemo(() => label.split('[')[0], [label]);

  const handleOnChange = useCallback((_: unknown, checked: boolean) => onChange(checked), [onChange]);
  const handleBlur = useCallback(
    ({ target: { value } }: FocusEvent<HTMLButtonElement>) => onBlur(id, value),
    [id, onBlur],
  );

  const handleFocus = useCallback(
    ({ target: { value } }: FocusEvent<HTMLButtonElement>) => onFocus(id, value),
    [id, onFocus],
  );

  return (
    <FormControl>
      <span className={checkboxWidgetClasses.root}>
        <FormControlLabel
          label={label || schema.title}
          classes={{
            root: checkboxWidgetClasses.labelRoot,
            label: checkboxWidgetClasses.label,
          }}
          control={
            <Checkbox
              id={id}
              checked={typeof value === 'undefined' ? false : value}
              classes={{
                root: checkboxWidgetClasses.checkboxRoot,
                colorSecondary: checkboxWidgetClasses.colorSecondary,
              }}
              required={required}
              disabled={disabled || readonly}
              autoFocus={autofocus}
              onChange={handleOnChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
            />
          }
        />
        <IconButton
          size="small"
          className={cx(commonWidgetClasses.widgetMenuButton, checkboxWidgetClasses.widgetMenuButton)}
          aria-controls="menu"
          aria-haspopup="true"
          onClick={handleClick}
        >
          <MoreHorizIcon />
        </IconButton>
      </span>
    </FormControl>
  );
};
